import last from "lodash/last";

import { PRODUCT_NAME_TO_ID_MAP } from "@/helpers/constants";
import { stringIsJson } from "@/helpers/formatting";

import type { ILenderPlacementResponse } from "@/models/funders";
import type { IPlacementTableRow } from "@/models/offers";
import type { PlacementFilters } from "@/models/placement";

export const isValidForSubmittedPlacements = (
  placement: IPlacementTableRow,
  filters?: PlacementFilters
) => {
  // Filter by product type
  if (filters?.selectedProductTypes?.length) {
    const placementProductType = placement.product_type;

    if (!placementProductType) {
      return true;
    }

    const placementProductId =
      PRODUCT_NAME_TO_ID_MAP[
        placementProductType.toLowerCase().replace(/ /g, "_")
      ];

    if (!placementProductId) {
      return true;
    }

    if (!filters.selectedProductTypes.includes(placementProductId)) {
      return false;
    }
  }

  if (filters?.selectedStatuses?.length) {
    const placementStatus = String(placement.status);
    if (!filters.selectedStatuses.includes(placementStatus)) {
      return false;
    }
  }

  return true;
};

type PlacementErrorSource = {
  decline_reason?: string | null;
  responses?: ILenderPlacementResponse[];
};

const flattenStringMap = (value: unknown): string[] => {
  if (typeof value === "string") {
    return [value];
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => flattenStringMap(item));
  }

  if (value && typeof value === "object") {
    return Object.values(value as Record<string, unknown>).flatMap((item) =>
      flattenStringMap(item)
    );
  }

  return [];
};

const parseResponsePayload = (response: unknown): string | null => {
  if (!response) {
    return null;
  }

  if (typeof response === "string") {
    if (!stringIsJson(response)) {
      return response;
    }

    return parseResponsePayload(JSON.parse(response));
  }

  if (typeof response !== "object") {
    return null;
  }

  const payload = response as Record<string, unknown>;

  const validation = payload["validation"];
  if (validation && typeof validation === "object") {
    const messages = flattenStringMap(validation);
    if (messages.length) {
      return messages.join(", ");
    }
  }

  const errors = payload["errors"] as
    | { validation_errors?: unknown }
    | undefined;
  const validationErrors = errors?.validation_errors;
  if (validationErrors) {
    const messages = flattenStringMap(validationErrors);
    if (messages.length) {
      return messages.join(", ");
    }
  }

  if (typeof payload["message"] === "string") {
    return payload["message"];
  }

  const nationalFundingError = (
    payload["ResponseStatus"] as
      | { ErrorInfo?: { Message?: string } }
      | undefined
  )?.ErrorInfo?.Message;
  if (nationalFundingError) {
    return nationalFundingError;
  }

  const leadErrorMessages = Object.values(payload).flatMap((entry) => {
    if (!entry || typeof entry !== "object") {
      return [];
    }
    const nestedErrors = (entry as Record<string, unknown>)["Errors"];
    if (!nestedErrors || typeof nestedErrors !== "object") {
      return [];
    }
    return Object.values(nestedErrors as Record<string, unknown>).flatMap(
      (errorEntry) => {
        if (errorEntry && typeof errorEntry === "object") {
          const message = (errorEntry as Record<string, unknown>)["Message"];
          if (typeof message === "string") {
            return [message];
          }
        }
        return [];
      }
    );
  });

  if (leadErrorMessages.length) {
    return leadErrorMessages.join(", ");
  }

  return null;
};

/**
 * Funder responses vary in shape (string JSON, nested objects, funder-specific
 * envelopes). Prefer parsing the latest raw funder response for the richest
 * available detail, and fall back to the normalized `decline_reason` when
 * parsing yields nothing.
 */
export const getPlacementErrorMessage = (
  placement: PlacementErrorSource
): string | null => {
  const latestResponse = last(placement.responses ?? [])?.response;
  const parsed = parseResponsePayload(latestResponse);
  if (parsed) {
    return parsed;
  }

  if (placement.decline_reason) {
    return placement.decline_reason;
  }

  return null;
};
